import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText,
  ListItemText
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { GET_SERVICE_REQUEST } from "../../../../graphql/serviceRequests/getServiceRequestsQuery";
import { GET_VEHICLE } from "../../../../graphql/vehicles/getVehicle";
import { ADD_SERVICE_REQUEST_NOTE } from "../../../../graphql/serviceRequests/addServiceRequestNoteMutation";
import { UPDATE_SERVICE_REQUEST } from "../../../../graphql/serviceRequests/updateServiceRequestMutation";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { CheckCircle, Schedule, Build, Cancel, HourglassEmpty, Info, CloudUpload } from "@material-ui/icons";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import * as UUID from "uuid";
import { returnfileContentTypeAndExtention, DATE_TYPE } from "../../../utils";
import { getSignedUrl } from "../../../../utils/getSignedUrl";
import { getLocalizedDateFormat } from "../../../../utils/localized.syntex";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3)
  },
  backButton: {
    marginRight: theme.spacing(2)
  },
  statusBadge: {
    marginLeft: theme.spacing(2),
    fontWeight: 600
  },
  card: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  detailLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: 12,
    textTransform: "uppercase",
    marginBottom: 4
  },
  issueCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: 8
  },
  imagePreview: {
    width: 100,
    height: 100,
    objectFit: "cover",
    margin: theme.spacing(1),
    borderRadius: 4,
    cursor: "pointer"
  },
  timeline: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    position: "relative",
    paddingTop: theme.spacing(2)
  },
  timelineItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    "&:not(:last-child):after": {
      content: '""',
      position: "absolute",
      left: "50%",
      top: 20,
      width: "100%",
      height: 2,
      backgroundColor: "#e0e0e0",
      zIndex: 0
    }
  },
  timelineIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 600,
    zIndex: 1,
    backgroundColor: "#fff"
  },
  timelineIconActive: {
    backgroundColor: theme.palette.primary.main,
    color: "white"
  },
  timelineIconCompleted: {
    backgroundColor: theme.palette.success.main,
    color: "white"
  },
  timelineIconPending: {
    backgroundColor: "#e0e0e0",
    color: "#999"
  },
  timelineIconDeclined: {
    backgroundColor: "#fdecea",
    color: "#d32f2f",
    border: "1px solid #f5c6cb"
  },
  noteCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f0f7ff",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 4
  },
  noteHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8
  },
  addNoteSection: {
    marginTop: theme.spacing(2)
  }
}));

const ANSWER_TYPE_LABELS: Record<string, string> = {
  TEXT: "Text",
  YES_NO: "Yes / No",
  DROPDOWN: "Dropdown",
  MULTI_SELECT: "Multi Select",
  FILE_UPLOAD: "Photo Upload"
};

const ServiceRequestDetails: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const showSnackbar = useSnackBar();
  const { t } = useTranslation();
  const { _e_ } = useSelector((state: IAppState) => state.authReducer);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const country = website?.organisation?.address?.country || "";
  const [newNote, setNewNote] = useState("");
  const [detailResponses, setDetailResponses] = useState<Record<string, { answer?: string; selectedOptions?: string[]; files?: string[] }>>({});
  const [detailUploadStatus, setDetailUploadStatus] = useState<Record<string, boolean>>({});

  const { data, loading, error } = useQuery(GET_SERVICE_REQUEST, {
    variables: { serviceRequestId: id },
    fetchPolicy: "cache-and-network"
  });

  const [addNote, { loading: addingNote }] = useMutation(ADD_SERVICE_REQUEST_NOTE, {
    refetchQueries: [
      { query: GET_SERVICE_REQUEST, variables: { serviceRequestId: id } }
    ]
  });
  
  const [updateServiceRequest, { loading: submittingAnswers }] = useMutation(UPDATE_SERVICE_REQUEST, {
    refetchQueries: [
      { query: GET_SERVICE_REQUEST, variables: { serviceRequestId: id } }
    ],
    awaitRefetchQueries: true
  });

  const serviceRequest = data?.serviceRequest;
  const [loadVehicle, { data: vehicleData }] = useLazyQuery(GET_VEHICLE, {
    fetchPolicy: "cache-first"
  });
  useEffect(() => {
    if (serviceRequest?.vehicleId) {
      loadVehicle({ variables: { vehicleId: serviceRequest.vehicleId } });
    }
  }, [loadVehicle, serviceRequest?.vehicleId]);
  const resolvedLicencePlate = vehicleData?.vehicle?.licencePlate;

  const branchTimeZone = website?.branches?.find((b: any) => b.id === serviceRequest?.branchId)?.timeZone;

  useEffect(() => {
    if (serviceRequest?.additionalDetails) {
      const initialResponses: Record<string, { answer?: string; selectedOptions?: string[]; files?: string[] }> = {};
      serviceRequest.additionalDetails.forEach((detail: any, index: number) => {
        const key = detail.questionId || `detail-${index}`;
        initialResponses[key] = {
          answer: detail.answer || "",
          selectedOptions: detail.selectedOptions || [],
          files: detail.files || []
        };
      });
      setDetailResponses(initialResponses);
      setDetailUploadStatus({});
    }
  }, [serviceRequest?.additionalDetails]);

  const getDetailKey = (detail: any, index: number) => detail.questionId || `detail-${index}`;

  const isDetailAnswered = (detail: any, key: string) => {
    const response = detailResponses[key];
    const answerType = detail.answerType || "TEXT";
    if (answerType === "FILE_UPLOAD") {
      const files = response?.files !== undefined ? response.files : detail.files;
      return Array.isArray(files) && files.length > 0;
    }
    if (answerType === "MULTI_SELECT") {
      const selected = response?.selectedOptions !== undefined ? response.selectedOptions : detail.selectedOptions;
      return Array.isArray(selected) && selected.length > 0;
    }
    const answer = response?.answer !== undefined ? response.answer : detail.answer;
    return Boolean(answer && answer.toString().trim());
  };

  const updateDetailResponse = (
    key: string,
    patch: Partial<{ answer?: string; selectedOptions?: string[]; files?: string[] }>
  ) => {
    setDetailResponses((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch
      }
    }));
  };

  const handleDetailFileUpload = async (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!_e_) {
      showSnackbar({
        message: t("root:uploadServiceNotAvailable"),
        variant: SnackBarVariant.ERROR
      });
      return;
    }

    setDetailUploadStatus((prev) => ({ ...prev, [key]: true }));
    const uploadedKeys: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith("image/")) {
          throw new Error(t("snackBar:uploadOnlyImageFiles"));
        }

        if (file.size > 5242880) {
          throw new Error(t("snackBar:imageSizeExceed5MB"));
        }

        const { fileExtension } = returnfileContentTypeAndExtention(file);
        const uniqueId = UUID.v4();
        const keyName = `service-request-answers/${uniqueId}.${fileExtension}`;
        
        await _e_.add({
          name: keyName,
          file: file,
          complete: async () => {
            uploadedKeys.push(keyName);
          },
          error: (err: any) => {
            throw new Error(`${t("snackBar:uploadFailed")}: ${err}`);
          }
        });
      }

      if (uploadedKeys.length > 0) {
        updateDetailResponse(key, {
          files: [
            ...(detailResponses[key]?.files || serviceRequest?.additionalDetails?.find((detail: any, index: number) => getDetailKey(detail, index) === key)?.files || []),
            ...uploadedKeys
          ]
        });
        showSnackbar({
          message: `${uploadedKeys.length} ${t("snackBar:imagesUploadedSuccessfully")}`,
          variant: SnackBarVariant.SUCCESS
        });
      }
    } catch (error: any) {
      showSnackbar({
        message: error.message || t("snackBar:failedToUploadImages"),
        variant: SnackBarVariant.ERROR
      });
    } finally {
      setDetailUploadStatus((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveDetailFile = (key: string, url: string) => {
    const existingFiles =
      detailResponses[key]?.files ||
      serviceRequest?.additionalDetails?.find((detail: any, index: number) => getDetailKey(detail, index) === key)?.files ||
      [];
    updateDetailResponse(key, {
      files: existingFiles.filter((file: string) => file !== url)
    });
  };

  const totalAdditionalQuestions = serviceRequest?.additionalDetails?.length || 0;
  const answeredAdditionalQuestions = serviceRequest?.additionalDetails
    ? serviceRequest.additionalDetails.filter((detail: any, idx: number) =>
        isDetailAnswered(detail, getDetailKey(detail, idx))
      ).length
    : 0;
  const unansweredAdditionalQuestions = totalAdditionalQuestions - answeredAdditionalQuestions;

  const hasPendingChanges = serviceRequest?.additionalDetails
    ? serviceRequest.additionalDetails.some((detail: any, index: number) => {
        const key = getDetailKey(detail, index);
        const response = detailResponses[key];
        if (!response) {
          return false;
        }
        const answerType = detail.answerType || "TEXT";
        switch (answerType) {
          case "FILE_UPLOAD": {
            const originalFiles = detail.files || [];
            const nextFiles =
              response.files !== undefined ? response.files : originalFiles;
            if (originalFiles.length !== nextFiles.length) return true;
            const sortedOriginal = [...originalFiles].sort();
            const sortedNext = [...nextFiles].sort();
            return sortedOriginal.some((file: string, idx: number) => file !== sortedNext[idx]);
          }
          case "MULTI_SELECT": {
            const originalSelected = detail.selectedOptions || [];
            const nextSelected =
              response.selectedOptions !== undefined ? response.selectedOptions : originalSelected;
            if (originalSelected.length !== nextSelected.length) return true;
            const sortedOriginal = [...originalSelected].sort();
            const sortedNext = [...nextSelected].sort();
            return sortedOriginal.some((value: string, idx: number) => value !== sortedNext[idx]);
          }
          default: {
            const originalAnswer = detail.answer || "";
            const nextAnswer =
              response.answer !== undefined ? response.answer : originalAnswer;
            return (originalAnswer || "").trim() !== (nextAnswer || "").trim();
          }
        }
      })
    : false;

  const isSubmitDisabled =
    submittingAnswers || (!hasPendingChanges && unansweredAdditionalQuestions === 0);

  const renderDetailInput = (detail: any, key: string) => {
    const answerType = detail.answerType || "TEXT";
    const response = detailResponses[key] || {};
    switch (answerType) {
      case "YES_NO":
        return (
          <RadioGroup
            row
            value={response.answer ?? ""}
            onChange={(e) => updateDetailResponse(key, { answer: e.target.value })}
          >
            <FormControlLabel value="Yes" control={<Radio color="primary" />} label={t("misc:yes")} />
            <FormControlLabel value="No" control={<Radio color="primary" />} label={t("misc:no")} />
          </RadioGroup>
        );
      case "DROPDOWN":
        return (
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t("root:selectOption")}</InputLabel>
            <Select
              value={response.answer ?? ""}
              onChange={(e) => updateDetailResponse(key, { answer: e.target.value as string })}
              label={t("root:selectOption")}
            >
              <MenuItem value="">
                <em>{t("root:selectOption")}</em>
              </MenuItem>
              {(detail.options || []).map((option: string) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "MULTI_SELECT":
        return (
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t("root:selectMultiple")}</InputLabel>
            <Select
              multiple
              value={response.selectedOptions ?? []}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]);
                updateDetailResponse(key, { selectedOptions: value, answer: value.join(", ") });
              }}
              label={t("root:selectMultiple")}
              renderValue={(selected) =>
                (selected as string[]).length
                  ? (selected as string[]).join(", ")
                  : t("root:selectMultiple")
              }
            >
              {(detail.options || []).map((option: string) => (
                <MenuItem key={option} value={option}>
                  <Checkbox
                    color="primary"
                    checked={(response.selectedOptions ?? []).indexOf(option) > -1}
                  />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "FILE_UPLOAD":
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={
                detailUploadStatus[key] ? <CircularProgress size={18} /> : <CloudUpload />
              }
              disabled={detailUploadStatus[key]}
            >
              {detailUploadStatus[key] ? t("root:uploading") : t("root:uploadPhoto")}
              <input
                type="file"
                accept="image/*"
                hidden
                multiple
                onChange={(e) => handleDetailFileUpload(key, e)}
              />
            </Button>
            {response.files && response.files.length > 0 && (
              <Box mt={1.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {response.files.map((fileKey: string, fileIndex: number) => (
                  <Chip
                    key={fileKey}
                    label={`${t("root:photo", "Photo")} ${fileIndex + 1}`}
                    onClick={async () => {
                      // get signedUrl for s3 key
                      const signedUrl = await getSignedUrl(fileKey);
                      if (signedUrl) {
                        window.open(signedUrl, "_blank");
                      }
                    }}
                    onDelete={() => handleRemoveDetailFile(key, fileKey)}
                    color="primary"
                    variant="outlined"
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            placeholder={t("root:typeAnswerHere")}
            value={response.answer ?? ""}
            onChange={(e) => updateDetailResponse(key, { answer: e.target.value })}
          />
        );
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showSnackbar({ message: t("snackBar:pleaseEnterNote"), variant: SnackBarVariant.ERROR });
      return;
    }

    try {
      await addNote({
        variables: {
          serviceRequestId: id,
          note: newNote,
          isCustomerVisible: true
        }
      });

      showSnackbar({ message: t("snackBar:noteAddedSuccessfully"), variant: SnackBarVariant.SUCCESS });
      setNewNote("");
    } catch (error: any) {
      showSnackbar({ message: error.message || t("snackBar:errorAddingNote"), variant: SnackBarVariant.ERROR });
    }
  };

  const handleSubmitAnswers = async () => {
    if (!serviceRequest?.additionalDetails) {
      return;
    }

    const compiledDetails = serviceRequest.additionalDetails.map((detail: any, index: number) => {
      const key = getDetailKey(detail, index);
      const response = detailResponses[key];
      const answerType = detail.answerType || "TEXT";
      const baseOptions = detail.options || [];
      const isMandatory = detail.isMandatory || false;

      let answer = detail.answer || "";
      let selectedOptions = detail.selectedOptions || [];
      let files = detail.files || [];

      if (answerType === "MULTI_SELECT") {
        if (response?.selectedOptions !== undefined) {
          selectedOptions = response.selectedOptions || [];
        }
        answer = selectedOptions.join(", ");
      } else if (answerType === "FILE_UPLOAD") {
        if (response?.files !== undefined) {
          files = response.files || [];
        }
      } else {
        if (response?.answer !== undefined) {
          answer = response.answer || "";
        }
      }

      return {
        questionId: detail.questionId,
        question: detail.question,
        answerType,
        options: baseOptions,
        isMandatory,
        answer,
        selectedOptions,
        files
      };
    });

    const missingMandatory = compiledDetails.filter((detail: any) => {
      if (!detail.isMandatory) return false;
      if (detail.answerType === "FILE_UPLOAD") {
        return !detail.files || detail.files.length === 0;
      }
      if (detail.answerType === "MULTI_SELECT") {
        return !detail.selectedOptions || detail.selectedOptions.length === 0;
      }
      return !detail.answer || !detail.answer.trim();
    }).length;

    if (missingMandatory > 0) {
      showSnackbar({
        message: t("snackBar:pleaseAnswerAllRemainingQuestions", {
          count: missingMandatory,
          plural: missingMandatory > 1 ? "s" : ""
        }),
        variant: SnackBarVariant.ERROR
      });
      return;
    }

    try {
      await updateServiceRequest({
        variables: {
          serviceRequest: {
            id,
            additionalDetails: compiledDetails,
            status: "Pending Approval"
          }
        }
      });

      showSnackbar({
        message: t("snackBar:answersSubmittedSuccessfully"),
        variant: SnackBarVariant.SUCCESS
      });
    } catch (error: any) {
      showSnackbar({
        message: error.message || t("snackBar:errorSubmittingAnswers"),
        variant: SnackBarVariant.ERROR
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Pending Approval":
      case "Awaiting More Information":
        return "warning";
      case "Approved":
      case "Completed":
        return "success";
      case "Declined":
        return "error";
      case "In Progress":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Pending Approval":
        return <HourglassEmpty fontSize="small" />;
      case "Approved":
        return <CheckCircle fontSize="small" />;
      case "Declined":
        return <Cancel fontSize="small" />;
      case "In Progress":
        return <Build fontSize="small" />;
      case "Awaiting More Information":
        return <Info fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const renderStatusTimeline = () => {
    const requestStatus = serviceRequest.status || "";
    const vehicleStatus = serviceRequest.vehicleService?.status || "";
    const normalizedRequestStatus = requestStatus.toUpperCase();
    const normalizedCurrentStatus = (vehicleStatus || requestStatus || "").toUpperCase();
    const isCancelled =
      normalizedRequestStatus === "CANCELLED" || normalizedCurrentStatus === "CANCELLED";

    const cancelledLabel = t("root:requestCancelled", {
      defaultValue: t("misc:cancelled", { defaultValue: "Cancelled" })
    });

    const timelineSteps =
      serviceRequest.status === "Declined"
        ? [
            {
              label: t("root:requestCreated"),
              date: serviceRequest.createdAt,
              state: "completed" as const
            },
            {
              label: t("root:requestDeclined"),
              date: serviceRequest.reviewedAt || serviceRequest.updatedAt || serviceRequest.createdAt,
              state: "declined" as const
            }
          ]
        : [
            {
              label: t("root:requestCreated"),
              date: serviceRequest.createdAt,
              state: "completed" as const
            },
            {
              label: t("root:underReview"),
              date: serviceRequest.createdAt,
              state:
                normalizedRequestStatus === "PENDING APPROVAL" ||
                normalizedRequestStatus === "AWAITING MORE INFORMATION"
                  ? "current"
                  : "completed"
            },
            {
              label: t("root:approved"),
              date: serviceRequest.reviewedAt || serviceRequest.vehicleService?.startDate,
              state:
                ["APPROVED", "IN PROGRESS", "COMPLETED", "CLOSED", "SCHEDULED", "CANCELLED"].includes(
                  normalizedCurrentStatus
                )
                  ? "completed"
                  : "pending"
            },
            {
              label: t("root:inProgress"),
              date:  serviceRequest.vehicleService?.startDate || serviceRequest.vehicleService?.appointmentDate ,
              state: ["IN PROGRESS", "COMPLETED", "CLOSED", "CANCELLED"].includes(normalizedCurrentStatus)
                ? "completed"
                : ["SCHEDULED", "APPROVED"].includes(normalizedCurrentStatus)
                ? "current"
                : "pending"
            },
            {
              label: isCancelled ? cancelledLabel : t("root:completed"),
              date: serviceRequest.vehicleService?.completionDate || serviceRequest.updatedAt,
              state: ["COMPLETED", "CLOSED"].includes(normalizedCurrentStatus)
                ? "completed"
                : isCancelled
                ? "declined"
                : "pending"
            }
          ];

    return (
      <Box className={classes.timeline}>
        {timelineSteps.map((step, index) => {
          let iconClass = classes.timelineIconPending;
          let iconContent: React.ReactNode = index + 1;

          if (step.state === "completed") {
            iconClass = classes.timelineIconCompleted;
            iconContent = "✓";
          } else if (step.state === "declined") {
            iconClass = classes.timelineIconDeclined;
            iconContent = <Cancel fontSize="small" />;
          } else if (step.state === "current") {
            iconClass = classes.timelineIconActive;
            iconContent = index + 1;
          }

          return (
            <Box key={index} className={classes.timelineItem}>
              <Box className={`${classes.timelineIcon} ${iconClass}`}>{iconContent}</Box>
              <Typography
                variant="body2"
                style={{
                  fontWeight: step.state === "completed" || step.state === "current" ? 600 : 400,
                  marginTop: 8,
                  textAlign: "center",
                  fontSize: 12
                }}
              >
                {step.label}
              </Typography>
              {step.date && (step.state === "completed" || step.state === "declined") && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ textAlign: "center", fontSize: 10 }}
                >
                  {getLocalizedDateFormat(country, step.date, DATE_TYPE.EXPANDED, branchTimeZone)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !serviceRequest) {
    return (
      <Box p={3}>
        <Typography color="error">
          {error?.message || t("snackBar:serviceRequestNotFound")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Button
            className={classes.backButton}
            onClick={() => history.push("/account/service-requests")}
          >
            ← {t("misc:back")}
          </Button>
          <Typography variant="h4">{t("root:serviceRequestDetails")}</Typography>
          <Chip
            label={serviceRequest.vehicleService?.status?.replace(/_/g, ' ') || serviceRequest.status}
            color={getStatusColor(serviceRequest.vehicleService?.status || serviceRequest.status) as any}
            icon={getStatusIcon(serviceRequest.vehicleService?.status || serviceRequest.status)}
            className={classes.statusBadge}
          />
        </Box>
      </Box>

      <Card className={classes.card}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography className={classes.detailLabel}>{t("root:referenceNumber")}</Typography>
              <Typography variant="h6">{serviceRequest.referenceNumber}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography className={classes.detailLabel}>{t("root:vehicle")}</Typography>
              <Typography>{resolvedLicencePlate || serviceRequest.vehicleId || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography className={classes.detailLabel}>{t("root:createdDate")}</Typography>
              <Typography>
                {getLocalizedDateFormat(country, serviceRequest.createdAt, DATE_TYPE.EXPANDED, branchTimeZone)}
              </Typography>
            </Grid>
            {serviceRequest.estimatedCost && (
              <Grid item xs={12} md={3}>
                <Typography className={classes.detailLabel}>{t("root:estimatedCost")}</Typography>
                <Typography>£{serviceRequest.estimatedCost.toFixed(2)}</Typography>
              </Grid>
            )}
            {serviceRequest.appointmentDate && (
                <Grid item xs={12} md={3}>
                  <Typography className={classes.detailLabel}>{t("root:appointmentDate")}</Typography>
                  <Typography>
                    {getLocalizedDateFormat(country, serviceRequest.appointmentDate, DATE_TYPE.CONDENSED, branchTimeZone)}
                  </Typography>
                </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("root:requestProgress")}
          </Typography>
          {renderStatusTimeline()}
        </CardContent>
      </Card>

      {((serviceRequest.status !== "Pending Approval" && serviceRequest.status !== "Declined") || serviceRequest.vehicleService) && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t("root:serviceDetails")}
            </Typography>
            <Grid container spacing={3}>
              {serviceRequest.vehicleService ? (
                <>
                  {serviceRequest.vehicleService.referenceNumber && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceReference")}</Typography>
                      <Typography variant="h6">{serviceRequest.vehicleService.referenceNumber}</Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.status && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceStatus")}</Typography>
                      <Chip
                        label={serviceRequest.vehicleService?.status?.replace(/_/g, ' ')}
                        color={getStatusColor(serviceRequest.vehicleService.status) as any}
                        size="small"
                      />
                    </Grid>
                  )}                  
                  {serviceRequest.vehicleService.serviceType && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceType")}</Typography>
                      <Typography>{serviceRequest.vehicleService.serviceType}</Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.serviceCategory && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceCategory")}</Typography>
                      <Typography>{serviceRequest.vehicleService.serviceCategory.replace(/_/g, ' ')}</Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.serviceProviderName && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceProvider")}</Typography>
                      <Typography>{serviceRequest.vehicleService.serviceProviderName}</Typography>
                      {serviceRequest.vehicleService.serviceProviderAddress?.fullAddress && (
                        <Typography variant="body2" color="textSecondary">
                          {serviceRequest.vehicleService.serviceProviderAddress.fullAddress}
                        </Typography>
                      )}
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.appointmentDate && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:appointmentDate")}</Typography>
                      <Typography>
                        {getLocalizedDateFormat(country, serviceRequest.vehicleService.appointmentDate, DATE_TYPE.EXPANDED, branchTimeZone)}
                      </Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.completionDate && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:completionDate")}</Typography>
                      <Typography>
                        {getLocalizedDateFormat(country, serviceRequest.vehicleService.completionDate, DATE_TYPE.EXPANDED, branchTimeZone)}
                      </Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.totalExpenses !== undefined && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:totalCost")}</Typography>
                      <Typography variant="h6" color="primary">
                        £{((serviceRequest.vehicleService.totalExpenses || 0) / 100).toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.odometerReading !== undefined && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:odometerReading")}</Typography>
                      <Typography>{serviceRequest.vehicleService.odometerReading.toLocaleString()} miles</Typography>
                    </Grid>
                  )}
                  {serviceRequest.vehicleService.description && (
                    <Grid item xs={12} md = {6}>
                      <Typography className={classes.detailLabel}>{t("root:description")}</Typography>
                      <Typography>{serviceRequest.vehicleService.description}</Typography>
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  {serviceRequest.serviceProviderName && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:serviceProvider")}</Typography>
                      <Typography>{serviceRequest.serviceProviderName}</Typography>
                    </Grid>
                  )}
                  {serviceRequest.appointmentDate && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:appointmentDate")}</Typography>
                      <Typography>
                        {getLocalizedDateFormat(country, serviceRequest.appointmentDate, DATE_TYPE.CONDENSED, branchTimeZone)}
                      </Typography>
                    </Grid>
                  )}
                  {serviceRequest.estimatedCompletionDate && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:estimatedCompletion")}</Typography>
                      <Typography>
                        {getLocalizedDateFormat(country, serviceRequest.estimatedCompletionDate, DATE_TYPE.CONDENSED, branchTimeZone)}
                      </Typography>
                    </Grid>
                  )}
                  {serviceRequest.estimatedCost && (
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.detailLabel}>{t("root:estimatedCost")}</Typography>
                      <Typography variant="h6" color="primary">
                        £{serviceRequest.estimatedCost.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {((serviceRequest.vehicleService?.jobSheet && serviceRequest.vehicleService.jobSheet.length > 0) || 
        (serviceRequest.jobSheet && serviceRequest.jobSheet.length > 0)) && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t("root:workToBeDone")}
            </Typography>
            {serviceRequest.vehicleService?.jobSheetStatus && (
              <Typography variant="body1" style={{ marginTop: '8px', marginBottom: '16px' }}>
                {t("root:status")} : <span style={{
                  color: serviceRequest.vehicleService.jobSheetStatus === "APPROVED" ? "green" :
                    serviceRequest.vehicleService.jobSheetStatus === "REJECTED" ? "red" : "orange"
                }}>{serviceRequest.vehicleService.jobSheetStatus === "APPROVED" ? "Approved" :
                  serviceRequest.vehicleService.jobSheetStatus === "REJECTED" ? "Rejected" : "Pending"}</span>
              </Typography>
            )}
            {(serviceRequest.vehicleService?.jobSheet || serviceRequest.jobSheet || []).map((item: any, index: number) => (
              <Box key={index} p={2} mb={1} bgcolor="#f9f9f9" borderRadius={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {item.name || item.task}
                    </Typography>
                    {(item.description || item.taskDescription) && (
                      <Typography variant="body2" color="textSecondary">
                        {item.description || item.taskDescription}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.detailLabel}>{t("root:cost")}</Typography>
                    <Typography>£{((item.cost || 0) / 100).toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={2}>
              <Typography variant="h6" align="right">
                {t("root:totalCost")}: £{(serviceRequest.vehicleService?.jobSheet || serviceRequest.jobSheet || [])
                  .reduce((sum: number, item: any) => sum + ((item.cost || 0) / 100), 0)
                  .toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {serviceRequest.status === "Declined" && (
        <Card className={classes.card} style={{ borderLeft: "4px solid #f44336" }}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle} color="error">
              {t("root:requestDeclined")}
            </Typography>
            <Box
              bgcolor="#fff5f5"
              border="1px solid #f8d7da"
              borderRadius={6}
              p={2}
              mb={serviceRequest.declineReason ? 2 : 0}
            >
              <Typography variant="body2" style={{ color: "#b71c1c", fontWeight: 500 }}>
                {t("root:hiThere")}
              </Typography>
              <Typography variant="body2" style={{ color: "#b71c1c" }}>
                {t("root:serviceRequestDeclinedMessage")}
              </Typography>
              {serviceRequest.declineReason ? (
                <Typography variant="body2" style={{ color: "#b71c1c", marginTop: 8 }}>
                  {t("root:whatWeNeed")} {serviceRequest.declineReason}.
                </Typography>
              ) : (
                <Typography variant="body2" style={{ color: "#b71c1c", marginTop: 8 }}>
                  {t("root:declineReasonGeneric")}
                </Typography>
              )}
              <Typography variant="body2" style={{ color: "#b71c1c", marginTop: 8 }}>
                {t("root:declineInstruction")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("root:issuesReported")}
          </Typography>
          {serviceRequest.issues?.map((issue: any, index: number) => (
            <Box key={index} className={classes.issueCard}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {issue.issueType}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {issue.description}
              </Typography>
              {issue.images && issue.images.length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap">
                  {issue.images.map((imgKey: string, imgIndex: number) => {
                    // load signedUrl on mounting
                    const loadImageUrl = () => {
                      getSignedUrl(imgKey).then((url) => {
                        if (url) {
                          const imgElement = document.querySelector(`img[data-img-key="${imgKey}"]`) as HTMLImageElement;
                          if (imgElement) {
                            imgElement.src = url;
                          }
                        }
                      });
                    };
                    loadImageUrl();
                    return (
                      <img
                        key={imgIndex}
                        data-img-key={imgKey}
                        src=""
                        alt={`${t("root:issue")} ${index + 1} - ${t("root:imageAlt")} ${imgIndex + 1}`}
                        className={classes.imagePreview}
                        onClick={async () => {
                          const signedUrl = await getSignedUrl(imgKey);
                          if (signedUrl) {
                            window.open(signedUrl, "_blank");
                          }
                        }}
                        onError={(e) => {
                          loadImageUrl();
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}

          {serviceRequest.comments && (
            <Box mt={2}>
              <Typography className={classes.detailLabel}>{t("root:additionalComments")}</Typography>
              <Typography variant="body2">{serviceRequest.comments}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("root:communication")}
          </Typography>

          {serviceRequest.notes && serviceRequest.notes.length > 0 ? (
            serviceRequest.notes
              .filter((note: any) => note.isCustomerVisible)
              .map((note: any, index: number) => (
                <Box key={index} className={classes.noteCard}>
                  <Box className={classes.noteHeader}>
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      {note.createdByName || t("root:serviceTeam")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getLocalizedDateFormat(country, note.createdAt, DATE_TYPE.EXPANDED, branchTimeZone)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{note.note}</Typography>
                </Box>
              ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              {t("root:noCommunicationYet")}
            </Typography>
          )}

          {serviceRequest.status !== "Closed" && serviceRequest.status !== "Declined" && (
            <Box className={classes.addNoteSection}>
              <Divider style={{ marginBottom: 16 }} />
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {t("root:addMessage")}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder={t("root:askQuestionPlaceholder")}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                >
                  {addingNote ? t("root:sending") : t("root:sendMessage")}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {serviceRequest.status === "Awaiting More Information" &&
        serviceRequest.additionalDetails &&
        serviceRequest.additionalDetails.length > 0 && (
          <Card className={classes.card} style={{ borderLeft: "4px solid #ff9800" }}>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle} style={{ color: "#ff9800" }}>
                {t("root:additionalInformationRequested")}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {t("root:provideFollowingInformation")}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {answeredAdditionalQuestions} {t("root:of")} {totalAdditionalQuestions}{" "}
                {t("root:customerAnsweredCount", "question(s) answered by customer")}
              </Typography>
              {serviceRequest.additionalDetails.map((detail: any, index: number) => {
                const key = getDetailKey(detail, index);
                const hasResponse = isDetailAnswered(detail, key);
                const existingAnswer = Boolean(
                  (detail.answer && detail.answer.trim()) ||
                  (detail.selectedOptions && detail.selectedOptions.length > 0) ||
                  (detail.files && detail.files.length > 0)
                );
                return (
                  <Box key={key} mb={3}>
                    <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {index + 1}. {detail.question}
                      </Typography>
                      {hasResponse ? (
                        <Chip
                          label={t("root:answered", "Answered")}
                          size="small"
                          color="primary"
                          style={{ height: 20, fontSize: 11 }}
                        />
                      ) : (
                        <Chip
                          label={t("root:pendingAnswer", "Awaiting response")}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          style={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {detail.isMandatory && (
                        <Chip
                          label={t("root:required")}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          style={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      <Chip
                        label={ANSWER_TYPE_LABELS[detail.answerType || "TEXT"] || detail.answerType || "Text"}
                        size="small"
                        variant="outlined"
                        style={{ height: 20, fontSize: 11 }}
                      />
                    </Box>
                    {existingAnswer ? (
                      <Box bgcolor="#f0f7ff" p={2} borderRadius={1} border="1px solid #90caf9">
                        {detail.answer && (
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            ✓ {t("root:yourAnswer")} {detail.answer}
                          </Typography>
                        )}
                        {detail.selectedOptions && detail.selectedOptions.length > 0 && (
                          <Box mt={detail.answer ? 1 : 0} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                            {detail.selectedOptions.map((option: string) => (
                              <Chip key={option} label={option} size="small" variant="outlined" />
                            ))}
                          </Box>
                        )}
                        {detail.files && detail.files.length > 0 && (
                          <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                            {detail.files.map((fileKey: string, fileIndex: number) => (
                              <Chip
                                key={fileKey}
                                label={`${t("root:photo")} ${fileIndex + 1}`}
                                onClick={async () => {
                                  // get signedURl for s3 key
                                  const signedUrl = await getSignedUrl(fileKey);
                                  if (signedUrl) {
                                    window.open(signedUrl, "_blank");
                                  }
                                }}
                                color="primary"
                                variant="outlined"
                                style={{ cursor: "pointer" }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        {renderDetailInput(detail, key)}
                        {detail.isMandatory && (
                          <FormHelperText style={{ color: "#d32f2f" }}>
                            {t("root:requiredField", "This response is required")}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}

              <Box mt={3} display="flex" flexDirection="column" style={{ gap: 12 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bgcolor="#f5faff"
                  border="1px solid #d0e4ff"
                  borderRadius={8}
                  px={2}
                  py={1.5}
                >
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {answeredAdditionalQuestions} / {totalAdditionalQuestions}{" "}
                    {t("root:questionsAnswered", "questions answered")}
                  </Typography>
                  {unansweredAdditionalQuestions > 0 ? (
                    <Typography variant="body2" color="secondary">
                      {t("root:pendingAnswer", "Awaiting response")}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="primary">
                      {hasPendingChanges
                        ? t("root:readyToSubmitChanges", "Ready to submit updates")
                        : t("root:questionsUpToDate", "All responses are up to date")}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSubmitAnswers}
                  disabled={isSubmitDisabled}
                  style={{ padding: "12px" }}
                >
                  {submittingAnswers ? t("root:submitting") : t("root:submitAnswers")}
                </Button>
                {!submittingAnswers && isSubmitDisabled && (
                  <Typography variant="caption" color="textSecondary" align="center">
                    {t(
                      "root:updateAnyAnswerToResubmit",
                      "Update any answer above if you need to send new information."
                    )}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
    </Box>
  );
};

export default ServiceRequestDetails;
